import { memo, useEffect, useState } from 'react';
import { CompactPicker } from 'react-color';
import { useSelector } from 'react-redux';

import { Card, ConfigProvider, Flex, Form, Input, InputNumber, Select } from 'antd';

import classnames from 'classnames';
import equal from 'fast-deep-equal';

import { numberWithCommas } from '../../../helpers/common';
import { isFieldAvailable } from '../../../helpers/subscription';

import { FORM_FIELD_TYPE } from '../../../constants/common';
import { FARM_EDIT_FIELDS } from '../../../constants/farm';
import { FEATURE_LIST } from '../../../constants/subscription';

import useCitiesByCountry from '../../../hooks/useCitiesByCountry';

import countries from '../../../countries.json';

const { TextArea } = Input;

function FarmEditForm({ color, editing, farm, formRef, handleColorChange, onChange, setSubmittable }) {
  const [form] = Form.useForm();

  const { myPlan } = useSelector(state => state.subscription);

  const [currentColor, setCurrentColor] = useState();
  const [currentCountry, setCurrentCountry] = useState(farm?.address?.country);
  const [initialFormValues, setInitialFormValues] = useState({});

  if (formRef) formRef.current = form;

  const values = Form.useWatch([], form);

  const formattedOrder = FARM_EDIT_FIELDS.filter(field =>
    isFieldAvailable(field.id, myPlan?.type, FEATURE_LIST.FARM_DETAILS_FIELDS)
  );

  const { citiesOptions, loading, error } = useCitiesByCountry(currentCountry);

  useEffect(() => {
    const initial = form.getFieldsValue(true);
    setInitialFormValues(initial);
  }, []);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable?.(true))
      .catch(() => setSubmittable?.(false));
  }, [values]);

  useEffect(() => {
    if (color) setCurrentColor(color);
  }, [color]);

  useEffect(() => {
    if (farm) {
      setCurrentCountry(farm?.address?.country);
      form.setFieldsValue(prepareFormValues());
    }
  }, [farm, editing]);

  function prepareFormValues() {
    if (!farm) return;

    const initValues = {};

    for (let field of formattedOrder) {
      let value = farm?.[field.id] ? farm?.[field.id] : field.type === FORM_FIELD_TYPE.NUMBER ? 0 : '';

      switch (field.id) {
        case 'street':
          value = farm['address'].street;
          break;
        case 'house_number':
          value = farm.address?.house_number;
          break;
        case 'city':
          value = farm.address?.city ? [farm.address?.city] : [];
          break;
        case 'postal_code':
          value = farm.address?.postal_code;
          break;
        case 'country':
          value = farm.address?.country || '';
          break;
        case 'circumference':
        case 'size':
          value = value ? numberWithCommas(value) : '/';
          break;
        case 'holding_numbers':
          value = Array.isArray(farm?.holding_numbers) ? farm?.holding_numbers?.at(0) : farm?.holding_numbers || '0';
          break;
        default:
          if (Array.isArray(value)) {
            if (editing) {
              if (field.id !== 'geofences') {
                value = value.map(e => e.name).join(', ');
              }
            } else {
              value = value
                .filter(e => !e.is_master)
                .map(e => e.name)
                .join(', ');
            }
          }
          break;
      }

      initValues[field.id] = value;
    }

    return initValues;
  }

  function changeCurrentColor({ hex }) {
    setCurrentColor(hex);
    handleColorChange(hex);
  }

  function handleChange(changedValues) {
    let [[changedKey, changedValue]] = Object.entries(changedValues);

    if (changedKey === 'city') {
      const city = citiesOptions.find(s => s.value === changedValue?.at(0));
      changedValue = city ? city?.label : changedValue;
      form.setFieldValue('city', changedValue);
    }

    if (changedKey === 'country') {
      form.setFieldValue('city', []);
      setCurrentCountry(changedValues?.country);
    }

    const current = form.getFieldsValue();
    const isSame = equal(current, initialFormValues);

    onChange?.(!isSame);
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          colorTextDisabled: 'rgba(0,0,0,0.88)'
        }
      }}>
      <Card title={`Farm ${farm?.name} Details`} className="mb-2">
        <Form
          autoComplete="off"
          className="antd-form"
          name="farm-edit"
          form={form}
          initialValues={prepareFormValues()}
          labelCol={{ span: 6 }}
          requiredMark={editing}
          onValuesChange={handleChange}>
          <Flex vertical={true} gap="small">
            {formattedOrder?.map(field => {
              return (
                <Form.Item
                  labelWrap
                  key={field.id}
                  label={field.name}
                  name={field.id}
                  colon={false}
                  labelAlign="left"
                  rules={field.rules}>
                  {field.type === FORM_FIELD_TYPE.SELECT ? (
                    <Select
                      className={classnames('country-select', { 'hidden-arrow': !editing })}
                      disabled={!editing}
                      options={countries.map(x => ({ value: x, label: x }))}
                      variant={editing ? 'outlined' : 'borderless'}
                      showSearch={true}
                    />
                  ) : field.type === FORM_FIELD_TYPE.TAGS ? (
                    <Select
                      className={classnames('country-select', { 'hidden-arrow': !editing })}
                      mode="tags"
                      maxCount={1}
                      optionFilterProp="label"
                      disabled={!editing || !!error || loading}
                      loading={loading}
                      options={citiesOptions}
                      showSearch={true}
                      variant={editing ? 'outlined' : 'borderless'}
                    />
                  ) : field.type === FORM_FIELD_TYPE.TEXTAREA ? (
                    <TextArea
                      className="textarea"
                      name={field.id}
                      disabled={!editing}
                      variant={editing ? 'outlined' : 'borderless'}
                    />
                  ) : field.type === FORM_FIELD_TYPE.NUMBER ? (
                    <InputNumber
                      changeOnWheel
                      className="w-full"
                      disabled={!editing || !field.editable}
                      min={0}
                      name={field.id}
                      variant={editing && field.editable ? 'outlined' : 'borderless'}
                    />
                  ) : (
                    <Input
                      disabled={!editing || !field.editable}
                      name={field.id}
                      variant={editing && field.editable ? 'outlined' : 'borderless'}
                    />
                  )}
                </Form.Item>
              );
            })}

            {editing && (
              <Form.Item label="Farm Color" labelAlign="left" name="color" colon={false}>
                <CompactPicker name="colour" color={currentColor} onChange={changeCurrentColor} />
              </Form.Item>
            )}
          </Flex>
        </Form>
      </Card>
    </ConfigProvider>
  );
}

export default memo(FarmEditForm);
